import { coerceLiteralNumberRequired, FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import type { CustomerOrderMetadataPolicy } from '~/lib/customer-order-metadata';
import z from 'zod';
import { TenantOrderContext } from '~/tenants/common/TenantOrderContext';
import { TenantOrder } from '~/tenants/common/TenantOrder';
import { DisplayData, TenantOrderLine } from '~/lib/model';
import Big from 'big.js';
import { datetime } from '~/lib/datettime';
import Mlm3dConfig from '~/tenants/mlm/performable/3d/Mlm3dConfig';
import { MlmCustomerMetadata } from '~/tenants/mlm/model/MlmCustomer';
import { MlmProviderStaticData } from '~/tenants/mlm/model/MlmProvider';
import MlmAerialConfig from '~/tenants/mlm/performable/aerial/MlmAerialConfig';
import MlmPhotosConfig, { MlmPhotosType } from '~/tenants/mlm/performable/photos/MlmPhotosConfig';
import MlmBrochuresConfig from '~/tenants/mlm/performable/brochures/MlmBrochuresConfig';
import MlmFloorplanConfig from '~/tenants/mlm/performable/floorplan/MlmFloorplanConfig';
import MlmFlyerConfig from '~/tenants/mlm/performable/flyer/MlmFlyerConfig';
import MlmHeadshotConfig from '~/tenants/mlm/performable/headshot/MlmHeadshotConfig';
import MlmVirtualStagingConfig from '~/tenants/mlm/performable/staging/MlmVirtualStagingConfig';
import MlmVirtualConfig from '~/tenants/mlm/performable/virtual/MlmVirtualConfig';
import MlmWalkthroughConfig from '~/tenants/mlm/performable/walkthrough/MlmWalkthroughConfig';
import MlmPhotosJob from '~/tenants/mlm/performable/photos/MlmPhotosJob';
import MlmFloorplanJob from '~/tenants/mlm/performable/floorplan/MlmFloorplanJob';

export enum MlmOrderType {
  RESIDENTIAL = 'residential',
  COMMERCIAL = 'commercial',
}

export enum MlmPropertyAccess {
  LATER = 'LATER',
  HOMEOWNER = 'HOMEOWNER',
  AGENT = 'AGENT',
  LOCKBOX = 'LOCKBOX',
  SENTRI = 'SENTRI',
}

export const MlmOrderSchema = {
  [FIRST_VERSION_TIMESTAMP]: z.object({
    version: coerceLiteralNumberRequired(FIRST_VERSION_TIMESTAMP),
    type: z.nativeEnum(MlmOrderType),
    access: z.nativeEnum(MlmPropertyAccess, { required_error: 'Please select the access type.' }),
    code: z.string().optional(),
    notes: z.string().optional(),
    sqft: z.coerce
      .number({
        required_error: 'Please provide the square footage of the property or unit.',
        invalid_type_error: 'Please provide the square footage of the property or unit.',
      })
      .min(1, { message: 'The square footage of the property or unit must be greater than 1.' }),
  }),
};

export type MlmOrderMetadata = ZodVersionedMetadata<typeof MlmOrderSchema>;

/**
 * The order fields an MLM customer may write (lib/customer-order-metadata.ts).
 * MlmOrderForm offers the Commercial type (hourly photos) to the office only.
 */
export const MLM_CUSTOMER_ORDER_METADATA: CustomerOrderMetadataPolicy = {
  customer: ['type', 'access', 'code', 'notes', 'sqft'],
  office: [],
  customerValues: { type: [MlmOrderType.RESIDENTIAL] },
};

export type MlmPerformableConfig =
  | typeof MlmAerialConfig
  | typeof MlmBrochuresConfig
  | typeof MlmVirtualStagingConfig
  | typeof MlmHeadshotConfig
  | typeof MlmWalkthroughConfig
  | typeof MlmFloorplanConfig
  | typeof MlmVirtualConfig
  | typeof MlmPhotosConfig
  | typeof MlmFlyerConfig
  | typeof Mlm3dConfig;

export type MlmOrderContext = TenantOrderContext<
  MlmOrderMetadata,
  MlmPerformableConfig,
  MlmCustomerMetadata,
  MlmProviderStaticData
>;

export class MlmOrder extends TenantOrder<MlmOrderContext> {
  info(): Array<DisplayData> {
    const info = super.info();

    const type = (() => {
      switch (this.context.metadata.type) {
        case MlmOrderType.RESIDENTIAL:
          return 'Real estate property';
        default:
          return undefined;
      }
    })();

    if (type) {
      info.push({
        name: 'Order Type',
        invoice: true,
        value: type,
        provider: true,
        schedule: true,
        customer: true,
      });
    }

    const access = (() => {
      switch (this.context.metadata.access) {
        case MlmPropertyAccess.LATER:
          return 'Later';
        case MlmPropertyAccess.HOMEOWNER:
          return 'Homeowner';
        case MlmPropertyAccess.AGENT:
          return 'Agent';
        case MlmPropertyAccess.LOCKBOX:
          return 'Lockbox';
        case MlmPropertyAccess.SENTRI:
          return 'Sentri';
        default:
          return undefined;
      }
    })();

    if (access) {
      info.push({
        name: 'Property Access',
        value: access,
        provider: true,
        schedule: true,
        customer: true,
      });
    }

    if (this.context.metadata.code) {
      info.push({
        name: 'Access Code',
        provider: true,
        customer: true,
        schedule: true,
        value: this.context.metadata.code,
      });
    }

    if (this.context.metadata.notes) {
      info.push({
        name: 'Notes',
        provider: true,
        schedule: true,
        value: this.context.metadata.notes,
      });
    }

    if (this.context.metadata.sqft) {
      info.push({
        name: 'Square Feet',
        invoice: true,
        provider: true,
        customer: true,
        schedule: true,
        value: this.context.metadata.sqft.toString(),
      });
    }

    return info;
  }

  revenueLines(): TenantOrderLine[] {
    const lines = super.revenueLines();
    const onsite = this.jobs.some((j) => j.onsite() > 0);

    if (onsite) {
      lines.push({
        id: 'travel',
        description: 'Travel Compensation',
        amount: new Big(5),
        fee: 'travel',
      });
    }

    if (this.requestedOnWeekend && this.context.appointments.length === 0) {
      lines.push({
        id: 'weekend-requested',
        description: 'Weekend Fee',
        amount: new Big(150),
        fee: 'weekend',
      });
    }

    for (const appointment of this.context.appointments) {
      const isOnWeekend = datetime(appointment.start, this.location.timezone).weekday >= 6;

      if (isOnWeekend) {
        lines.push({
          id: `weekend-${appointment.id}`,
          description: 'Weekend Fee',
          amount: new Big(150),
          fee: 'weekend',
        });
      }
    }

    const hasPhotos = this.jobs.some(
      (j) => j.performable.id === 'photos' && j.metadata.type === MlmPhotosType.STANDARD,
    );
    const hasFloorplan = this.jobs.some((j) => j.performable.id === 'floorplan');
    const hasAerials = this.jobs.some((j) => j.performable.id === 'aerial');
    const has3d = this.jobs.some((j) => j.performable.id === '3d');

    if (hasFloorplan && hasPhotos) {
      if (hasAerials) {
        if(has3d){
          lines.push({
            id: 'letsgo',
            description: 'Let’s Go!! Package Discount',
            amount: new Big(-230),
            discount: true,
          });
        }else {
          lines.push({
            id: 'heat',
            description: 'Heatin’ Up! Package Discount',
            amount: new Big(-130),
            discount: true,
          });
        }
      } else {
        lines.push({
          id: 'starter',
          description: 'Digital Starter Add-on Package Discount',
          amount: new Big(-40),
          discount: true,
        });
      }
    }

    return lines;
  }
}
